import React, { useState } from 'react';
import {
    ResponsiveContainer, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Spinner } from '@material-tailwind/react';
import { BoxesIcon, AlertTriangleIcon, ArrowDownCircleIcon, ArrowUpCircleIcon } from 'lucide-react';
import { useGetInventoryDashboard } from '../../custom-hooks/use-inventory';
import { convertToPresentableText } from '../../utils/functions';
import { CATEGORY_META, TRANSACTION_TYPE_META } from './utils';
import ItemCard from './components/ItemCard';
import AddTransactionModal from './components/AddTransactionModal';

const STATUS_CRITICAL = '#d03b3b'; // reserved status red — only ever means "at/over limit"
const STOCK_GRAY = '#E6DDCF'; // reference/ceiling bars, never a real data series

const StatCard = ({ icon: Icon, label, value, tone = 'brand' }) => {
    const toneClasses = {
        brand: 'bg-inv-brand-light text-inv-brand',
        warn: 'bg-red-50 text-red-600',
        good: 'bg-emerald-50 text-emerald-600',
    };
    return (
        <div className="rounded-2xl border border-inv-cream-200 bg-white p-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${toneClasses[tone]}`}>
                <Icon size={20} />
            </div>
            <div className="mt-3 text-2xl font-extrabold leading-none text-inv-ink">{value}</div>
            <div className="mt-1 text-xs font-medium text-[#8C8175]">{label}</div>
        </div>
    );
};

const ChartCard = ({ title, subtitle, children, empty, emptyText = 'No data yet', height = 220 }) => (
    <div className="rounded-2xl border border-inv-cream-200 bg-white p-5">
        <div className="mb-4">
            <h3 className="font-bold text-inv-ink">{title}</h3>
            {subtitle && <p className="text-xs text-[#8C8175]">{subtitle}</p>}
        </div>
        {empty ? (
            <div className="flex items-center justify-center text-sm text-[#8C8175]" style={{ height }}>{emptyText}</div>
        ) : (
            <div className="w-full" style={{ height }}>{children}</div>
        )}
    </div>
);

const tooltipStyle = { borderRadius: 12, border: '1px solid #E6DDCF', fontSize: 12, boxShadow: '0 12px 24px -12px rgba(40,22,8,0.25)' };

const DashboardView = ({ onNavigate }) => {
    const { data, isLoading, isError } = useGetInventoryDashboard();
    const [logItem, setLogItem] = useState(null);

    if (isLoading) {
        return <div className="flex min-h-[40vh] items-center justify-center"><Spinner className="h-8 w-8 text-inv-brand" /></div>;
    }
    if (isError) {
        return <div className="py-10 text-center text-[#8C8175]">Failed to load the inventory dashboard.</div>;
    }

    const stats = data?.stats || {};
    const trend = data?.trend || [];
    const itemBreakdown = data?.itemBreakdown || [];
    const items = (data?.items || []).slice().sort((a, b) => (a.reorderLevel > 0 && a.currentRemaining <= a.reorderLevel ? -1 : 1));

    // Today's consumption per item — colored by each item's own category so items
    // in the same category still cluster visually even though grouped by item.
    const todayByItem = itemBreakdown.map((b) => ({
        ...b,
        label: b.name,
        hex: CATEGORY_META[b.category]?.hex || '#898781',
    }));

    // Current stock per item, top 10 by quantity so the chart stays readable.
    const stockByItem = items
        .slice()
        .sort((a, b) => b.currentRemaining - a.currentRemaining)
        .slice(0, 10)
        .map((item) => ({ name: item.name, total: item.currentRemaining, hex: CATEGORY_META[item.category]?.hex || '#898781' }));

    const stockByCategory = Object.values(
        items.reduce((acc, item) => {
            const key = item.category;
            if (!acc[key]) acc[key] = { category: key, label: CATEGORY_META[key]?.label || convertToPresentableText(key), total: 0 };
            acc[key].total += item.currentRemaining;
            return acc;
        }, {})
    );

    // Items with a daily max set, today's usage vs that limit — flagged red at/over it.
    const dailyMaxUsage = items
        .filter((item) => item.dailyMaxLimit)
        .map((item) => {
            const usage = itemBreakdown.find((b) => b.itemId === item._id)?.total || 0;
            return { name: item.name, usage, max: item.dailyMaxLimit, exceeds: usage >= item.dailyMaxLimit };
        })
        .sort((a, b) => b.usage / b.max - a.usage / a.max);
    const manyDailyMaxItems = dailyMaxUsage.length > 6;

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <StatCard icon={BoxesIcon} label="Active Items" value={stats.totalItems ?? 0} tone="brand" />
                <StatCard icon={AlertTriangleIcon} label="Low Stock" value={stats.lowStockCount ?? 0} tone="warn" />
                <StatCard icon={ArrowUpCircleIcon} label="Today's IN" value={stats.todayIn ?? 0} tone="good" />
                <StatCard icon={ArrowDownCircleIcon} label="Today's OUT" value={stats.todayOut ?? 0} tone="brand" />
            </div>

            <ChartCard
                title="Daily Usage vs Daily Max"
                subtitle="Today's usage against each item's daily limit — red means at or over"
                empty={!dailyMaxUsage.length}
                emptyText="No items have a daily max limit set yet"
                height={260}
            >
                <ResponsiveContainer width="100%" height="100%" debounce={200}>
                    <BarChart data={dailyMaxUsage} margin={{ top: 8, right: 8, left: -20, bottom: manyDailyMaxItems ? 24 : 0 }} barGap={4} barCategoryGap="26%">
                        <CartesianGrid vertical={false} stroke="#e1e0d9" />
                        <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: '#898781' }}
                            axisLine={{ stroke: '#c3c2b7' }}
                            tickLine={false}
                            interval={0}
                            angle={manyDailyMaxItems ? -25 : 0}
                            textAnchor={manyDailyMaxItems ? 'end' : 'middle'}
                            height={manyDailyMaxItems ? 50 : 30}
                        />
                        <YAxis tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: '#F1E9DC' }} />
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                        <Bar dataKey="usage" name="Today's Usage" radius={[4, 4, 0, 0]} maxBarSize={24}>
                            {dailyMaxUsage.map((entry, i) => (
                                <Cell key={i} fill={entry.exceeds ? STATUS_CRITICAL : TRANSACTION_TYPE_META.out.hex} />
                            ))}
                        </Bar>
                        <Bar dataKey="max" name="Daily Max" radius={[4, 4, 0, 0]} maxBarSize={24} fill={STOCK_GRAY} />
                    </BarChart>
                </ResponsiveContainer>
            </ChartCard>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <ChartCard title="7-Day Activity" subtitle="Stock in, out, and breakage across all items" empty={!trend.length}>
                        <ResponsiveContainer width="100%" height="100%" debounce={200}>
                            <AreaChart data={trend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke="#e1e0d9" />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { weekday: 'short' })}
                                    tick={{ fontSize: 11, fill: '#898781' }}
                                    axisLine={{ stroke: '#c3c2b7' }}
                                    tickLine={false}
                                />
                                <YAxis tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={tooltipStyle} labelFormatter={(d) => new Date(d).toLocaleDateString()} />
                                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                                <Area type="monotone" dataKey="in" name={TRANSACTION_TYPE_META.in.label} stroke={TRANSACTION_TYPE_META.in.hex} fill={TRANSACTION_TYPE_META.in.hex} fillOpacity={0.1} strokeWidth={2} dot={{ r: 3 }} />
                                <Area type="monotone" dataKey="out" name={TRANSACTION_TYPE_META.out.label} stroke={TRANSACTION_TYPE_META.out.hex} fill={TRANSACTION_TYPE_META.out.hex} fillOpacity={0.1} strokeWidth={2} dot={{ r: 3 }} />
                                <Area type="monotone" dataKey="breakage" name={TRANSACTION_TYPE_META.breakage.label} stroke={TRANSACTION_TYPE_META.breakage.hex} fill={TRANSACTION_TYPE_META.breakage.hex} fillOpacity={0.1} strokeWidth={2} dot={{ r: 3 }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                <ChartCard title="Today by Item" subtitle="What's being consumed" empty={!todayByItem.length}>
                    <ResponsiveContainer width="100%" height="100%" debounce={200}>
                        <PieChart>
                            <Tooltip contentStyle={tooltipStyle} />
                            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: '#52514e' }} layout="vertical" verticalAlign="middle" align="right" />
                            <Pie data={todayByItem} dataKey="total" nameKey="label" innerRadius={50} outerRadius={75} paddingAngle={2} strokeWidth={2} stroke="#fff">
                                {todayByItem.map((entry) => <Cell key={entry.itemId} fill={entry.hex} />)}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <ChartCard title="Current Stock by Item" subtitle="Top 10 items by remaining quantity" empty={!stockByItem.length}>
                        <ResponsiveContainer width="100%" height="100%" debounce={200}>
                            <BarChart data={stockByItem} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barCategoryGap="30%">
                                <CartesianGrid vertical={false} stroke="#e1e0d9" />
                                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: '#F1E9DC' }} />
                                <Bar dataKey="total" name="Remaining" radius={[4, 4, 0, 0]} maxBarSize={24}>
                                    {stockByItem.map((entry, i) => <Cell key={i} fill={entry.hex} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                <ChartCard title="Stock by Category" subtitle="Current remaining quantity, grouped" empty={!stockByCategory.length}>
                    <ResponsiveContainer width="100%" height="100%" debounce={200}>
                        <PieChart>
                            <Tooltip contentStyle={tooltipStyle} />
                            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: '#52514e' }} layout="vertical" verticalAlign="middle" align="right" />
                            <Pie data={stockByCategory} dataKey="total" nameKey="label" innerRadius={50} outerRadius={75} paddingAngle={2} strokeWidth={2} stroke="#fff">
                                {stockByCategory.map((entry) => <Cell key={entry.category} fill={CATEGORY_META[entry.category]?.hex || '#898781'} />)}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <div>
                <div className="mb-3 flex items-center justify-between">
                    <h3 className="font-bold text-inv-ink">Items</h3>
                    <button onClick={() => onNavigate?.('items')} className="text-sm font-semibold text-inv-brand hover:underline">View all</button>
                </div>
                {items.length ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {items.slice(0, 8).map((item) => (
                            <ItemCard key={item._id} item={item} onLog={() => setLogItem(item)} onOpen={() => onNavigate?.('items')} />
                        ))}
                    </div>
                ) : (
                    <div className="rounded-2xl border border-dashed border-inv-cream-200 py-10 text-center text-sm text-[#8C8175]">
                        No items yet — add your first item from the Items tab.
                    </div>
                )}
            </div>

            <AddTransactionModal isOpen={!!logItem} onClose={() => setLogItem(null)} item={logItem} />
        </div>
    );
};

export default DashboardView;
